import type {
  ServiceQualificationResponse,
  QualificationStatus,
  QualificationRouteResult,
  QualificationRouterInput,
  QualificationSuccessStoreUpdate,
  LeadCaptureContext,
  CartState,
} from '@t/serviceQualification.types';
import { LeadCaptureReason } from '@t/serviceQualification.types';

const PRODUCT_OFFERS_STATUSES: ReadonlySet<QualificationStatus> = new Set(['ACTIVE', 'SERVICEABLE', 'RETIRED']);

const ADDRESS_NOT_FOUND_CONTEXT: LeadCaptureContext = {
  defaultCustomerType: 'residential',
  reason: LeadCaptureReason.ADDRESS_NOT_FOUND,
};

const buildSuccessStoreUpdate = (
  result: ServiceQualificationResponse,
  leadCaptureContext: LeadCaptureContext | null
): QualificationSuccessStoreUpdate => {
  const customerType =
    leadCaptureContext === null
      ? null
      : leadCaptureContext.defaultCustomerType === 'business'
        ? 'BUSINESS'
        : 'RESIDENTIAL';

  return {
    resolvedAddressId: result.resolvedAddressId,
    isCreateWorkOrderManually: result.createWorkOrderManually,
    qualificationResult: result,
    onNet: result.onNet,
    addressType: result.serviceQualificationItem.addressType,
    qualificationStatus: result.serviceQualificationItem.addressQualificationStatus,
    leadCaptureContext,
    leadCaptureReason: leadCaptureContext?.reason ?? null,
    customerType,
  };
};

/**
 * Maps a qualification outcome — a response, the empty-array result, or the
 * AMS-404 error — to a routing destination plus the store-update payload the
 * caller applies. Pure: no store access, no side effects.
 *
 * Non-404 errors never reach this router; they surface through the hook's
 * `onError` callback and the consumer renders the error state.
 */
export const qualificationRouter = (input: QualificationRouterInput): QualificationRouteResult => {
  // Address not found — either the API returned an empty array or AMS
  // answered 404. The guard must run before any destructuring below.
  if (Array.isArray(input) || input === 'AMS-404') {
    return {
      destination: 'LEAD_CAPTURE',
      leadCaptureContext: ADDRESS_NOT_FOUND_CONTEXT,
      storeUpdate: {
        qualificationResult: null,
        leadCaptureContext: ADDRESS_NOT_FOUND_CONTEXT,
        leadCaptureReason: LeadCaptureReason.ADDRESS_NOT_FOUND,
      },
    };
  }

  const result: ServiceQualificationResponse = input;
  const { resolvedAddressId } = result;
  const { addressQualificationStatus: status, addressType } = result.serviceQualificationItem;
  const isOnNet = result.onNet;

  // Multi-unit parent detected by AMS — force a unit-number prompt before routing
  if (result.isParentAddress) {
    return { destination: 'REQUEST_UNIT' };
  }

  const routeToLeadCapture = (leadCaptureContext: LeadCaptureContext): QualificationRouteResult => ({
    destination: 'LEAD_CAPTURE',
    leadCaptureContext,
    storeUpdate: buildSuccessStoreUpdate(result, leadCaptureContext),
  });

  // NON_BOOKABLE overrides all other routing — go directly to Lead Capture
  if (status === 'NON_BOOKABLE') {
    return routeToLeadCapture({
      defaultCustomerType: addressType === 'BUSINESS' ? 'business' : 'residential',
      reason: LeadCaptureReason.NON_BOOKABLE,
    });
  }

  // Off-net → Lead Capture
  if (!isOnNet) {
    return routeToLeadCapture({
      defaultCustomerType: addressType === 'BUSINESS' ? 'business' : 'residential',
      reason: addressType === 'BUSINESS' ? LeadCaptureReason.BUSINESS : LeadCaptureReason.OFF_NET,
    });
  }

  // On-net + Business → always Lead Capture
  if (addressType === 'BUSINESS') {
    return routeToLeadCapture({
      defaultCustomerType: 'business',
      reason: LeadCaptureReason.BUSINESS,
    });
  }

  // On-net + Residential + product-landing-eligible status
  if (PRODUCT_OFFERS_STATUSES.has(status)) {
    return { destination: 'PRODUCT_LANDING', resolvedAddressId, storeUpdate: buildSuccessStoreUpdate(result, null) };
  }

  // On-net + Residential + PLANNED
  if (status === 'PLANNED') {
    return routeToLeadCapture({
      defaultCustomerType: 'residential',
      reason: LeadCaptureReason.PLANNED,
    });
  }

  // On-net + Residential + CONSTRUCTION
  if (status === 'CONSTRUCTION') {
    return routeToLeadCapture({
      defaultCustomerType: 'residential',
      reason: LeadCaptureReason.CONSTRUCTION,
    });
  }

  // Fallback → address not found (BE-drift defence: the response is still a
  // full object, so it takes the full success write, not the not-found write)
  return routeToLeadCapture({
    defaultCustomerType: 'residential',
    reason: LeadCaptureReason.ADDRESS_NOT_FOUND,
  });
};

// ---------------------------------------------------------------------------
// Cart helpers — used by CheckAvailabilityDialog until Phase 3 migrates
// cart detection to the resume endpoint.
// ---------------------------------------------------------------------------

const IN_PROGRESS_CART_STATES: ReadonlySet<CartState> = new Set([
  'CART_IN_PROGRESS',
  'CHECKOUT_INITIATED',
  'SUBMITTED',
]);

export const needsInProgressModal = (cartState: CartState): boolean => IN_PROGRESS_CART_STATES.has(cartState);
